import React, { useState, useEffect, useRef } from 'react';
import { Beaker, ThermometerSnowflake, Thermometer, Star, Recycle, Plus } from 'lucide-react';
import { Starter, STARTER_TEXTURES, MANAGEMENT_STRATEGIES } from '../../types/baking';
import { checkStarterHealth, checkAndNotifyStarterFeeding } from '../../lib/utils';
import { StarterDetailsModal } from './StarterDetailsModal';
import { StarterFeedModal } from './StarterFeedModal';
import { Toast } from '../ui/Toast';
import { updateStarter } from '../../lib/store';
import { getCurrentActivity } from './StarterUtils';
import { auth, db } from '../../lib/firebase';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';

const getStarterImage = (activityLevel: number) => {
  if (activityLevel <= 20) return '/starter/starterlist10.png';
  if (activityLevel <= 40) return '/starter/starterlist30.png';
  if (activityLevel <= 80) return '/starter/starterlist60.png';
  return '/starter/starterlist100.png';
};

const getNextFeedingTime = (starter: Starter) => {
  if (!starter.lastFed) return null;

  const strategy = MANAGEMENT_STRATEGIES[starter.managementStrategy || 'normal'];
  const baseInterval = starter.storage === 'room' 
    ? strategy.feedingInterval.room 
    : strategy.feedingInterval.fridge;

  const lastFed = new Date(starter.lastFed);
  const nextFeeding = new Date(lastFed);
  nextFeeding.setHours(nextFeeding.getHours() + baseInterval);

  const now = new Date();
  const diff = nextFeeding.getTime() - now.getTime();
  
  if (diff <= 0) return 'Overdue';
  
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days}d ${remainingHours}h`;
  }
  return `${hours}h`;
};

interface StarterListProps {
  starters: Starter[];
  onUpdateStarter: (id: string, starter: Partial<Starter>) => void;
  onCreateStarter: () => void;
}

export default function StarterList({ starters, onUpdateStarter, onCreateStarter }: StarterListProps) {
  const [selectedStarter, setSelectedStarter] = useState<Starter | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [fedStarterName, setFedStarterName] = useState('');
  const [recentlyFed, setRecentlyFed] = useState<{[key: string]: boolean}>({});
  const [lastFedTimes, setLastFedTimes] = useState<{[key: string]: Date}>({});
  const [lastFedStarterId, setLastFedStarterId] = useState<string | null>(null);
  const [previousFedTime, setPreviousFedTime] = useState<Date | null>(null);
  const [urgentFeedStarter, setUrgentFeedStarter] = useState<Starter | null>(null);
  const [lastModalShownTime, setLastModalShownTime] = useState<{[key: string]: number}>({});
  const [starterImages, setStarterImages] = useState<{[key: string]: string}>({});
  const imageUpdateInterval = useRef<NodeJS.Timeout | null>(null);

  // Initialize and update starter images
  useEffect(() => {
    // Initial image mapping
    const updateStarterImages = () => {
      const newImages: {[key: string]: string} = {};
      
      starters.forEach(starter => {
        const hoursSinceLastFeed = 
          (new Date().getTime() - new Date(starter.lastFed).getTime()) / (1000 * 60 * 60);
        
        const activityLevel = getCurrentActivity(
          hoursSinceLastFeed,
          starter.storage,
          starter.roomTemperature,
          starter.feedingRatio
        );
        
        newImages[starter.id] = getStarterImage(activityLevel);
      });
      
      setStarterImages(newImages);
    };
    
    // Update images immediately
    updateStarterImages();
    
    // Set up interval to update images every minute
    imageUpdateInterval.current = setInterval(updateStarterImages, 60000);
    
    // Clean up interval on unmount
    return () => {
      if (imageUpdateInterval.current) {
        clearInterval(imageUpdateInterval.current);
      }
    };
  }, [starters]);

  useEffect(() => {
    const checkStarters = () => {
      const now = Date.now();
      
      for (const starter of starters) {
        const health = checkStarterHealth(starter);
        if (health.requiresUrgentFeed && !recentlyFed[starter.id]) {
          const lastShown = lastModalShownTime[starter.id] || 0;
          if (now - lastShown >= 60 * 60 * 1000) { // Check every 1 hour
            setUrgentFeedStarter(starter);
            setLastModalShownTime(prev => ({
              ...prev,
              [starter.id]: now
            }));

            // Notification creation moved to backend (checkStarterFeedings.ts)
            break;
          }
        }
      }
    };

    checkStarters();
    const interval = setInterval(checkStarters, 1800000); // Check every 30 minutes

    return () => clearInterval(interval);
  }, [starters, recentlyFed, lastModalShownTime]);

  const canFeedStarter = (starterId: string, lastFed: Date | null) => {
    if (recentlyFed[starterId]) return false;
    if (!lastFed) return true;
    
    const now = new Date().getTime();
    const lastFedTime = new Date(lastFed).getTime();
    const hoursSinceLastFeed = (now - lastFedTime) / (1000 * 60 * 60);
    return hoursSinceLastFeed >= 4;
  };

  const handleFeedClick = async (e: React.MouseEvent, starterId: string, starterName: string) => {
    e.stopPropagation();

    const now = new Date();
    const starter = starters.find(s => s.id === starterId);
    if (!starter) return;

    try {
      setLastFedStarterId(starterId);
      setPreviousFedTime(starter.lastFed);

      await updateStarter(starterId, { lastFed: now });

      setLastFedTimes(prev => ({ ...prev, [starterId]: now }));
      setRecentlyFed(prev => ({ ...prev, [starterId]: true }));

      setFedStarterName(starterName);
      setShowToast(true);

      // Mark all unprocessed notifications for this starter as processed
      if (auth.currentUser) {
        const notificationsRef = collection(db, 'notifications');
        const q = query(
          notificationsRef,
          where('userId', '==', auth.currentUser.uid),
          where('type', '==', 'starter_feeding'),
          where('data.starterId', '==', starterId),
          where('processed', '==', false)
        );

        const notificationDocs = await getDocs(q);
        const updatePromises = notificationDocs.docs.map(notificationDoc =>
          updateDoc(doc(db, 'notifications', notificationDoc.id), { processed: true })
        );

        await Promise.all(updatePromises);
        console.log(`Marked ${notificationDocs.size} notifications as processed for starter ${starterId}`);
      }

      setTimeout(() => {
        setRecentlyFed(prev => ({ ...prev, [starterId]: false }));
      }, 4 * 60 * 60 * 1000);

      if (urgentFeedStarter?.id === starterId) {
        setUrgentFeedStarter(null);
      }
    } catch (error) {
      console.error('Error updating feed time:', error);
    }
  };

  const handleUndoFeed = async () => {
    if (!lastFedStarterId || previousFedTime === null) return;

    try {
      await updateStarter(lastFedStarterId, { lastFed: previousFedTime });
      setLastFedTimes(prev => ({ ...prev, [lastFedStarterId]: previousFedTime }));
      setRecentlyFed(prev => ({ ...prev, [lastFedStarterId]: false }));
      setLastFedStarterId(null);
      setPreviousFedTime(null);
    } catch (error) {
      console.error('Error undoing feed:', error);
    }
  };

  const handleFavoriteClick = async (e: React.MouseEvent, starterId: string) => {
    e.stopPropagation();
    try {
      const currentFavorite = starters.find(s => s.isFavorite);
      if (currentFavorite) {
        await updateStarter(currentFavorite.id, { isFavorite: false });
        onUpdateStarter(currentFavorite.id, { isFavorite: false });
      }

      await updateStarter(starterId, { isFavorite: true });
      onUpdateStarter(starterId, { isFavorite: true });
    } catch (error) {
      console.error('Error updating favorite status:', error);
    }
  };

  if (starters.length === 0) {
    return (
      <div className="h-[500px] bg-black/10 border border-dashed border-white p-8 text-center flex flex-col justify-center items-center">
        <Beaker className="w-12 h-12 text-white/70 mb-4" strokeWidth={1}/>
        <h3 className="text-lg font-medium mb-2 text-white">No Starters Yet</h3>
        <p className="text-white/70 mb-6">
          Create your first starter to begin tracking its feeding schedule.
        </p>
        <button
          onClick={onCreateStarter}
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500/90 to-amber-600/90 hover:from-amber-400 hover:to-amber-600 text-white rounded-xl transition-all duration-300"
        >
          <Plus className="w-5 h-5" />
          CREATE YOUR FIRST STARTER
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {starters.map(starter => {
          const textureInfo = STARTER_TEXTURES.find(t => t.id === starter.texture);
          const health = checkStarterHealth(starter);
          const StorageIcon = starter.storage === 'fridge' ? ThermometerSnowflake : Thermometer;
          const canFeed = canFeedStarter(starter.id, starter.lastFed);
          const displayLastFed = lastFedTimes[starter.id] || starter.lastFed;
          const hoursSinceLastFeed = (new Date().getTime() - new Date(starter.lastFed).getTime()) / (1000 * 60 * 60);
          const nextFeeding = getNextFeedingTime(starter);
          const starterImage = starterImages[starter.id] || getStarterImage(0); // Fallback image if not yet in state
          
          return (
            <div
              key={starter.id}
              onClick={() => setSelectedStarter(starter)}
              className="h-[500px] bg-gradient-to-br from-[#414141] to-[#8C8C8C] shadow-lg cursor-pointer hover:shadow-xl transition-shadow relative overflow-hidden hover:border hover:border-amber-400 hover:shadow-xl"
            >
              <div className="absolute inset-0">
                <img
                  src={starterImage}
                  alt="Starter visualization"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>

              <div className="relative p-6 flex flex-col h-full">
                <div>
                  <div className="text-center">
                    <h3 className="text-2xl text-white font-brand font-light italic">{starter.name}</h3>
                  </div>
                
                  {/* 2-Column Layout */}
                  <div className="grid grid-cols-2 mt-2">
                    {/* Left Column */}
                    <div className="flex flex-col text-right mr-2">
                      {/* Next Feed */}
                      <div className="flex items-center justify-end">
                        <p className="text-white text-xs">Next feed</p>
                        <p className="text-lg text-amber-400 font-brand font-light italic ml-2">
                          {health.status === 'critical' ? 'Overdue' : nextFeeding}
                        </p>
                      </div>

                      {/* Storage Mode */}
                      <span className="text-white text-xs flex items-center justify-end">
                        {starter.storage === 'fridge' ? 'Refrigerated' : 'Room temp.'}
                      </span>
                    </div>
                
                    {/* Right Column */}
                    <div className="flex flex-col border-l border-white pl-2 mt-1.5">
                      {/* Texture Info */}
                      <span className="text-[#E1B3B2] text-xs font-bold uppercase flex items-center gap-1">
                        {textureInfo?.label}
                        <span className="text-white/50">•</span>
                        {starter.feedingRatio?.replace(/-/g, ':') || ''}
                      </span>
                
                      {/* Management Option */}
                      <p className="text-white text-xs text-left mt-1.5">{starter.managementStrategy || 'Normal'} mode</p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons - Now at the bottom */}
                <div className="mt-auto flex justify-center gap-4">
                  <div className="rounded-full bg-black/30 p-1">
                  <button
                    onClick={(e) => handleFeedClick(e, starter.id, starter.name)}
                    disabled={!canFeed}
                    className={`p-2 transition-colors ${
                      canFeed 
                        ? 'hover: text-white hover:text-amber-400' 
                        : 'cursor-not-allowed text-white opacity-50'
                    }`}
                    title={canFeed ? "Feed this starter" : "Starter was fed less than 4 hours ago"}
                  >
                    <Recycle className="w-5 h-5" strokeWidth={1} />
                  </button>
                  <button
                    onClick={(e) => handleFavoriteClick(e, starter.id)}
                    className="p-2 transition-colors border-l border-white/50"
                    aria-label={starter.isFavorite ? "Remove from favorites" : "Add to favorites"}
                  >
                    {starter.isFavorite ? (
                      <Star className="w-5 h-5 text-yellow-400" fill="#FBBF24" strokeWidth={1} />
                    ) : (
                      <Star className="w-5 h-5 text-white hover:text-yellow-400" strokeWidth={1} />
                    )}
                  </button>
                  </div>  
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <StarterDetailsModal
        starter={selectedStarter}
        starters={starters}
        onClose={() => setSelectedStarter(null)}
        onUpdate={onUpdateStarter}
      />

      <StarterFeedModal
        starter={urgentFeedStarter!}
        isOpen={!!urgentFeedStarter}
        onClose={() => setUrgentFeedStarter(null)}
        onFeed={async () => {
          if (urgentFeedStarter) {
            await handleFeedClick(
              new Event('click') as React.MouseEvent,
              urgentFeedStarter.id,
              urgentFeedStarter.name
            );
            setUrgentFeedStarter(null);
          }
        }}
      />

      {showToast && (
        <Toast
          message={`${fedStarterName} has been fed`}
          onClose={() => setShowToast(false)}
          onUndo={handleUndoFeed}
          duration={5000}
        />
      )}
    </>
  );
}

export { StarterList }